/**
 * Reserve panel — displays, edits, and syncs reserve values.
 *
 * Three-way sync:
 *   Sharedo (system of record) ↔ panel inputs ↔ Word document SDT controls
 *
 * Toolbar actions:
 *   Read from Doc  — reads advice.reserve.* content controls → populates inputs
 *   AI Assist      — reads document body text → calls AI → shows suggestions
 *   Save to Doc    — writes current input values → Word content controls
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Button,
  Input,
  Badge,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircle20Regular } from '@fluentui/react-icons/svg/checkmark-circle';
import { Dismiss20Regular } from '@fluentui/react-icons/svg/dismiss';
import { DocumentSync20Regular } from '@fluentui/react-icons/svg/document-sync';
import { Save20Regular } from '@fluentui/react-icons/svg/save';
import { Sparkle20Regular } from '@fluentui/react-icons/svg/sparkle';
import { useGateway } from '../../api/useGateway';
import type { ReserveData } from '../../api/gateway-client';
import { readReserveValues, writeReserveValues, readDocumentText } from '../../office/sdt-reader';

interface ReservePanelProps {
  matterId: string | null;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  toolbar: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    padding: '4px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '4px',
  },
  categoryHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
    fontWeight: 600,
    fontSize: '12px',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  subtotalRow: {
    fontWeight: 600,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  colAmount: { width: '100px', textAlign: 'right' },
  colSaved: { width: '80px', textAlign: 'right' },
  grandTotal: {
    fontWeight: 700,
    backgroundColor: '#075156',
    color: '#ffffff',
    padding: '8px',
    borderRadius: '4px',
    marginTop: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
  },
  cellRight: { textAlign: 'right' },
  savedValue: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  amountInput: {
    width: '90px',
    '& input': { textAlign: 'right', fontSize: '12px' },
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '32px 16px',
    color: tokens.colorNeutralForeground3,
  },
  aiPanel: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    borderRadius: '6px',
    padding: '10px',
    backgroundColor: tokens.colorBrandBackground2,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  aiHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  aiTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontWeight: 600,
    fontSize: '12px',
    color: tokens.colorBrandForeground1,
  },
  aiSuggestionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
    padding: '2px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  aiActions: {
    display: 'flex',
    gap: '6px',
    marginTop: '4px',
  },
  confidence: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  statusBar: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    padding: '2px 0',
    minHeight: '18px',
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAUD(value: number): string {
  return value.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 });
}

function parseInput(raw: string): number {
  const cleaned = raw.replace(/[$,\s]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : Math.max(0, n);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ReservePanel: React.FC<ReservePanelProps> = ({ matterId }) => {
  const styles = useStyles();
  const gateway = useGateway();

  // Sharedo data (system of record)
  const [sharedo, setSharedo] = useState<ReserveData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Editable local amounts (accountCode → number)
  const [amounts, setAmounts] = useState<Record<string, number>>({});

  // Toolbar state
  const [docStatus, setDocStatus] = useState<string>('');
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [docBusy, setDocBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);

  // AI state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<{ reserves: Array<{ accountCode: string; amount: number }>; confidence: number } | null>(null);

  // Load Sharedo data whenever matterId changes
  useEffect(() => {
    if (!gateway || !matterId) {
      setSharedo(null);
      setAmounts({});
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    gateway.getReserves(matterId)
      .then((data) => {
        if (cancelled) return;
        setSharedo(data);
        // Seed editable amounts from Sharedo values
        const seed: Record<string, number> = {};
        data.categories.forEach((cat) => cat.rows.forEach((r) => { seed[r.accountCode] = r.amount; }));
        setAmounts(seed);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load reserves');
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [gateway, matterId]);

  // Update a single row amount
  const setAmount = useCallback((accountCode: string, value: number) => {
    setAmounts((prev) => ({ ...prev, [accountCode]: value }));
  }, []);

  // Read from document
  const handleReadFromDoc = useCallback(async () => {
    setDocBusy(true);
    setDocStatus('Reading document...');
    try {
      const scan = await readReserveValues();
      if (scan.reserveControls === 0) {
        setDocStatus('No reserve content controls found in document');
        return;
      }
      const updates: Record<string, number> = {};
      scan.reserves.forEach((r) => { updates[r.accountCode] = r.amount; });
      setAmounts((prev) => ({ ...prev, ...updates }));
      setDocStatus(`Read ${scan.reserveControls} control${scan.reserveControls !== 1 ? 's' : ''} from document`);
    } catch (err: unknown) {
      setDocStatus(err instanceof Error ? err.message : 'Failed to read document');
    } finally {
      setDocBusy(false);
    }
  }, []);

  // Save to document
  const handleSaveToDoc = useCallback(async () => {
    setSaveBusy(true);
    setSaveStatus('Writing to document...');
    try {
      await writeReserveValues(amounts);
      setSaveStatus('Saved to document');
    } catch (err: unknown) {
      setSaveStatus(err instanceof Error ? err.message : 'Failed to write to document');
    } finally {
      setSaveBusy(false);
    }
  }, [amounts]);

  // AI Assist
  const handleAiAssist = useCallback(async () => {
    if (!gateway || !matterId) return;
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    try {
      const documentText = await readDocumentText();
      const result = await gateway.suggestReserves({ matterId, documentText });
      setAiResult({
        reserves: result.reserves.map((r) => ({ accountCode: r.accountCode, amount: r.amount })),
        confidence: result.confidence,
      });
    } catch (err: unknown) {
      setAiError(err instanceof Error ? err.message : 'AI suggestion failed');
    } finally {
      setAiLoading(false);
    }
  }, [gateway, matterId]);

  // Accept all AI suggestions
  const handleAcceptAll = useCallback(() => {
    if (!aiResult) return;
    const updates: Record<string, number> = {};
    aiResult.reserves.forEach((r) => { updates[r.accountCode] = r.amount; });
    setAmounts((prev) => ({ ...prev, ...updates }));
    setAiResult(null);
  }, [aiResult]);

  // Accept a single AI suggestion
  const handleAcceptOne = useCallback((accountCode: string, amount: number) => {
    setAmount(accountCode, amount);
    setAiResult((prev) => {
      if (!prev) return null;
      const remaining = prev.reserves.filter((r) => r.accountCode !== accountCode);
      return remaining.length > 0 ? { ...prev, reserves: remaining } : null;
    });
  }, [setAmount]);

  // ---------------------------------------------------------------------------
  // Early returns
  // ---------------------------------------------------------------------------

  if (!matterId) {
    return (
      <div className={styles.emptyState}>
        <Text>Select a matter first to view reserves</Text>
      </div>
    );
  }

  if (isLoading) return <Spinner label="Loading reserves..." size="small" />;

  if (loadError) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{loadError}</MessageBarBody>
      </MessageBar>
    );
  }

  if (!sharedo || sharedo.categories.length === 0) {
    return (
      <div className={styles.container}>
        <MessageBar intent="warning">
          <MessageBarBody>
            No reserve categories found. Reserve configuration must be deployed to the PMS instance first.
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Compute totals from local amounts
  // ---------------------------------------------------------------------------

  const grandTotal = Object.values(amounts).reduce((s, v) => s + v, 0);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={styles.container}>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <Tooltip content="Read reserve values from document content controls" relationship="label">
          <Button
            size="small"
            appearance="subtle"
            icon={<DocumentSync20Regular />}
            onClick={handleReadFromDoc}
            disabled={docBusy}
          >
            Read from Doc
          </Button>
        </Tooltip>
        <Tooltip content="Use AI to suggest reserve values from document content" relationship="label">
          <Button
            size="small"
            appearance="subtle"
            icon={<Sparkle20Regular />}
            onClick={handleAiAssist}
            disabled={aiLoading || !gateway}
          >
            {aiLoading ? 'Analysing...' : 'AI Assist'}
          </Button>
        </Tooltip>
        <Tooltip content="Write current values back to document content controls" relationship="label">
          <Button
            size="small"
            appearance="subtle"
            icon={<Save20Regular />}
            onClick={handleSaveToDoc}
            disabled={saveBusy}
          >
            Save to Doc
          </Button>
        </Tooltip>
      </div>

      {/* Status messages */}
      {(docStatus || saveStatus) && (
        <div className={styles.statusBar}>
          {saveStatus || docStatus}
        </div>
      )}

      {/* AI result panel */}
      {aiLoading && <Spinner label="AI is analysing the document..." size="small" />}
      {aiError && (
        <MessageBar intent="error">
          <MessageBarBody>{aiError}</MessageBarBody>
        </MessageBar>
      )}
      {aiResult && (
        <div className={styles.aiPanel}>
          <div className={styles.aiHeader}>
            <span className={styles.aiTitle}>
              <Sparkle20Regular />
              AI Suggestions
              <span className={styles.confidence}>
                ({Math.round(aiResult.confidence * 100)}% confidence)
              </span>
            </span>
            <Button size="small" appearance="transparent" icon={<Dismiss20Regular />} onClick={() => setAiResult(null)} />
          </div>
          {aiResult.reserves.filter((r) => r.amount > 0).map((r) => (
            <div key={r.accountCode} className={styles.aiSuggestionRow}>
              <span>{r.accountCode.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <strong>{formatAUD(r.amount)}</strong>
                <Button
                  size="small"
                  appearance="transparent"
                  icon={<CheckmarkCircle20Regular />}
                  onClick={() => handleAcceptOne(r.accountCode, r.amount)}
                  title="Accept"
                />
              </span>
            </div>
          ))}
          {aiResult.reserves.filter((r) => r.amount > 0).length === 0 && (
            <Text size={200}>No non-zero suggestions from AI.</Text>
          )}
          <div className={styles.aiActions}>
            <Button size="small" appearance="primary" onClick={handleAcceptAll}>
              Accept All
            </Button>
            <Button size="small" appearance="secondary" onClick={() => setAiResult(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Reserve table */}
      <Table size="extra-small" aria-label="Reserve breakdown">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Category</TableHeaderCell>
            <TableHeaderCell className={styles.colAmount}>Amount</TableHeaderCell>
            <TableHeaderCell className={styles.colSaved}>Saved</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sharedo.categories.map((category) => {
            const catTotal = category.rows.reduce((s, r) => s + (amounts[r.accountCode] ?? r.amount), 0);
            return (
              <React.Fragment key={category.code}>
                <TableRow>
                  <TableCell colSpan={3}>
                    <Text className={styles.categoryHeader}>{category.name}</Text>
                  </TableCell>
                </TableRow>
                {category.rows.map((row) => {
                  const current = amounts[row.accountCode] ?? row.amount;
                  const saved = row.amount;
                  const hasSuggestion = aiResult?.reserves.find((r) => r.accountCode === row.accountCode && r.amount > 0);
                  return (
                    <TableRow key={row.accountCode}>
                      <TableCell>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {row.label}
                          {hasSuggestion && (
                            <Badge size="tiny" appearance="filled" color="brand" title={`AI suggests ${formatAUD(hasSuggestion.amount)}`}>
                              AI
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className={styles.colAmount}>
                        <Input
                          className={styles.amountInput}
                          size="small"
                          value={current === 0 ? '' : String(current)}
                          placeholder="$0"
                          onChange={(_e, d) => setAmount(row.accountCode, parseInput(d.value))}
                          appearance="underline"
                        />
                      </TableCell>
                      <TableCell className={styles.colSaved}>
                        <span className={styles.savedValue}>{formatAUD(saved)}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className={styles.subtotalRow}>
                  <TableCell><Text weight="semibold">Subtotal</Text></TableCell>
                  <TableCell className={styles.colAmount}>{formatAUD(catTotal)}</TableCell>
                  <TableCell className={styles.colSaved}>
                    <span className={styles.savedValue}>{formatAUD(category.subtotal)}</span>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

      <div className={styles.grandTotal}>
        <span>GRAND TOTAL</span>
        <span>{formatAUD(grandTotal)}</span>
      </div>
    </div>
  );
};
